import React, { FC, useState } from "react";
import { useDialogShadow } from "@evergis/ui";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { dialogResourceSelector } from "../../../../../ducks/dialogs/selectors";
import { isEditable } from "../../../../../evergis/core/permissions";
import { formatNumber } from "../../../../../utils/formatNumber";
import { LinearProgress, LoadingDescription } from "../../../BulkCopy/styled";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { DialogTitle } from "../../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../../DialogComponents/FullScreenDialog";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";

import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { DataField, FieldValue, styledHeaderText } from "../../../styled";
import { useCopyProjectJob } from "./hooks/useCopyProjectJob";
import { useCopyProjectProps } from "./hooks/useCopyProjectProps";
import { TableStructureInfo, useGetTableStructure } from "./hooks/useGetTableStructure";
import { TableStructureControls, useTableStructureControls } from "./hooks/useTableStructureControls";
import { CopyProjectOptions } from "./CopyProjectOptions";
import { useProjectInfoByName } from "../../../../../hooks/project/useProjectByName";

export const ProjectCopy: FC = () => {
  const { t } = useTranslation();
  const projectToCopy: string = useSelector(dialogResourceSelector);
  const [project] = useProjectInfoByName(projectToCopy);
  const { limits, isCopyCurrentProject, actualDeps } = useCopyProjectProps();
  const isProjectEditable: boolean = isEditable(project.permissions);
  const isCopySharedProject: boolean = isCopyCurrentProject && !isProjectEditable;
  const [filteredLayers, setFilteredLayers] = useState([]);
  const { deps, meta, updateMeta, onApply, waiting, loading } = useCopyProjectJob(filteredLayers, isCopySharedProject);
  const { shadows, setScrollContainer } = useDialogShadow();
  const { layersCount } = project;
  const { layers, tables } = deps || {};
  const { layers: actualLayers, tables: actualTables } = actualDeps;

  const tableStructure: TableStructureInfo[] = useGetTableStructure(
    isCopyCurrentProject ? actualTables : tables,
    isCopyCurrentProject ? actualLayers : layers,
  );

  const tableStructureControls: TableStructureControls = useTableStructureControls(tableStructure, setFilteredLayers);

  return (
    <FullScreenDialog
      dialogContext={{
        title: {
          text: styledHeaderText(t("copyResource.copyProject", { ns: "dialogs" })),
          icon: "copy",
        },
        meta: {
          aliasLabel: t("copyResource.projectName", { ns: "dialogs" }),
          descriptionLabel: t("copyResource.projectDescription", { ns: "dialogs" }),
          loaderLabel: t("copyResource.uploadProjectPreview", { ns: "dialogs" }),
        },
        applyText: t("actions.copy", { ns: "common" }),
      }}
    >
      <DialogTitle displayShadow={shadows.upper} />
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          {!meta.alias || !tableStructure || loading ? (
            <>
              <LoadingDescription>{t("copyResource.gettingProjectData", { ns: "dialogs" })}</LoadingDescription>
              <LinearProgress />
            </>
          ) : (
            <>
              <ProjectMetaBlock>
                <ResourceMeta meta={meta} updateMeta={updateMeta}>
                  <DataField>
                    <FieldValue
                      field={t("copyResource.totalLayers", { ns: "dialogs" })}
                      value={formatNumber(layersCount)}
                    />
                  </DataField>
                </ResourceMeta>
              </ProjectMetaBlock>
              <CopyProjectOptions
                deps={deps}
                tableStructure={tableStructure}
                tableStructureControls={tableStructureControls}
                limits={limits}
              />
            </>
          )}
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyDisabled={!meta.alias || !tableStructure}
        onApply={() => onApply(tableStructureControls && tableStructureControls.treeState)}
        isWaiting={waiting}
      />
    </FullScreenDialog>
  );
};
